import { useCurrentUser } from '@cma/app'
import {
  Avatar,
  Button,
  classNames,
  Menu,
  Modal,
  Spinner,
  toast
} from '@cma/common'
import {
  ContentSharePartsFragment,
  ShareTargetInput
} from '@cma/generated/graphql'
import { HelixIcon } from '@helix/helix-icon'
import {
  chevron_down as ChevronDownIcon,
  document_pdf as DocumentPdfIcon,
  folder as FolderIcon
} from '@helix/helix-icon/outlined'
import { useEffect, useState } from 'react'
import { OrgSelect } from './OrganizationSelect'
import { useContent } from '../ContentContext'
import { usePlatformContent } from '../usePlatformContent'
import { useSharePlatformClientResources } from './hooks/useSharePlatformClientResources'
import { useUnsharePlatformClientResource } from './hooks/useUnsharePlatformClientResource'
import { getShareableContentName } from '../utils'

interface ShareContentModalProps {
  open: boolean
  ids?: {
    id: string
    type: 'Folder' | 'PlatformClientContent'
  }[]
  managePermissions?: string | null
  onClose: () => void
}

export function ShareContentModal({
  ids,
  managePermissions,
  open,
  onClose
}: ShareContentModalProps) {
  const { deselectAllFiles } = useContent()
  const { data: { currentUser } = {} } = useCurrentUser()
  const { data: { platformContent } = {} } = usePlatformContent({
    id: currentUser?.account?.id || ''
  })
  const {
    mutate: sharePlatformClientResources,
    isLoading: isSharingClientResources
  } = useSharePlatformClientResources({
    accountId: currentUser?.account?.id || ''
  })

  const [shareTargets, setShareTargets] = useState<ShareTargetInput[]>([])
  const [selectedContentId, setSelectedContentId] = useState<string | null>(
    null
  )

  useEffect(() => {
    if (managePermissions && !selectedContentId) {
      setSelectedContentId(managePermissions)
    }
  }, [selectedContentId, managePermissions])

  const handleClose = () => {
    onClose()
    deselectAllFiles()
    setSelectedContentId(null)
  }

  const shareableContents = [
    ...(platformContent?.customContents || []),
    ...(platformContent?.folders || []),
    ...(platformContent?.folders?.flatMap(
      (folder) => folder.customPages || []
    ) || [])
  ]?.filter((content) => {
    return ids?.map((content) => content.id)?.includes(content.id)
  })

  const combinedContentShares =
    shareableContents?.flatMap((content) => content.contentShares || []) || []

  const combinedTarget = shareableContents?.[0]
    ? {
        ...shareableContents[0],
        contentShares: combinedContentShares
      }
    : undefined

  if (!ids?.length && !managePermissions) {
    return null
  }

  return (
    <>
      <Modal size="xl" isOpen={open} onClose={handleClose}>
        <Modal.Title weight={600}>Share</Modal.Title>
        <div className="mt-6 flex flex-col gap-2">
          {shareableContents?.map((content) => (
            <div key={content.id}>
              <div className="flex w-full items-center justify-between rounded-md bg-gray-100 p-2">
                <div className="flex w-full items-center gap-3">
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    {content.__typename === 'PlatformClientContent' ? (
                      <HelixIcon icon={DocumentPdfIcon} />
                    ) : (
                      <HelixIcon icon={FolderIcon} />
                    )}
                    <span className="helix-body min-w-0 flex-1 truncate">
                      {content?.displayName || 'Untitled'}
                    </span>
                  </div>
                </div>
              </div>
              {Boolean(content?.contentShares?.length) && (
                <div className="mt-4 flex flex-col">
                  <label htmlFor="brand-name" className="helix-label">
                    Shared With
                  </label>
                  <ul className="my-1.5 space-y-1.5">
                    {content?.__typename === 'PlatformClientContent' && (
                      <li className="flex w-full items-center justify-between gap-3 p-1">
                        <Avatar
                          shadow
                          size="sm"
                          src={content?.uploader?.avatar || undefined}
                          name={content?.uploader?.name}
                        />
                        <div className="flex flex-col">
                          <span className="helix-body--strong">
                            {content?.uploader?.name || ''}
                            {content?.uploaderId === currentUser?.id
                              ? ' (You)'
                              : ''}
                          </span>
                          <span className="helix-small text-gray-500">
                            {content?.uploader?.email || ''}
                          </span>
                        </div>
                        <div className="helix-body ml-auto text-gray-500">
                          Owner
                        </div>
                      </li>
                    )}
                    {(content?.contentShares || []).map((shareable) => (
                      <SharableContentItem
                        accountId={currentUser?.account?.id}
                        key={shareable.id}
                        item={shareable}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
          <div className="mt-6">
            <div className="flex flex-col">
              <div className="helix-label mb-1">
                <span>Share With</span>
              </div>
              <OrgSelect
                filterSelected={true}
                selectedTarget={combinedTarget}
                onChange={(targets) => setShareTargets(targets)}
                onModalCloseClick={() => {
                  handleClose()
                }}
              />
            </div>
          </div>
          <p className="helix-small mb-4 mt-5 text-gray-500">
            This document will appear while creating a new report, during the
            customize step, under the additional content section.
          </p>
          <div className="-mx-6 flex items-center justify-end !border-t !border-solid !border-gray-100 px-5 pt-5">
            <Button
              type="button"
              disabled={!shareableContents?.length || !shareTargets?.length}
              loading={isSharingClientResources}
              onClick={() => {
                sharePlatformClientResources(
                  {
                    shareableContents: shareableContents.map((content) => ({
                      id: content.id,
                      type:
                        content.__typename === 'PlatformClientContent'
                          ? 'CustomPage'
                          : 'Folder'
                    })),
                    shareTargets
                  },
                  {
                    onSuccess() {
                      toast('Contents shared successfully.', {
                        variant: 'success'
                      })
                      handleClose()
                    },
                    onError(err) {
                      toast(err.message, {
                        variant: 'error'
                      })
                    }
                  }
                )
              }}>
              Share
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

interface SharableContentItemProps {
  item: ContentSharePartsFragment
  accountId?: string
}

function SharableContentItem({ item, accountId }: SharableContentItemProps) {
  const {
    mutate: unsharePlatformClientResource,
    isLoading: isUnsharingClientResource
  } = useUnsharePlatformClientResource({
    accountId: accountId || ''
  })

  if (!item.shareable) {
    return null
  }

  const name = getShareableContentName(item)

  return (
    <li
      className={classNames(
        'flex w-full items-center justify-between gap-3 p-1',
        {
          'animate-pulse': isUnsharingClientResource
        }
      )}>
      <Avatar shadow size="sm" rounded={false} name={name} />
      <div className="flex flex-col">
        <span className="helix-body--strong">
          <span className="helix-body text-gray-500">
            Anyone {item.shareableType === 'Team' ? 'on' : 'at'}
          </span>{' '}
          {name}
        </span>
        <span className="helix-small text-gray-500">
          {item.shareable.usersCount} users
        </span>
      </div>
      <div className="ml-auto">
        <Menu
          button={
            <Button
              loading={isUnsharingClientResource}
              variant={isUnsharingClientResource ? 'danger' : 'ghost'}
              rightIcon={
                !isUnsharingClientResource ? (
                  <HelixIcon icon={ChevronDownIcon} className="h-4 w-4" />
                ) : undefined
              }
              size="xxs">
              {isUnsharingClientResource ? (
                <div className="helix-text-brand h-4 w-4">
                  <Spinner />
                </div>
              ) : (
                <span className="helix-body--strong">Can Use</span>
              )}
            </Button>
          }>
          <Menu.Item>
            <div className="w-[150px]">
              <div className="helix-body text-gray-900">Can Use</div>
              <div className="helix-small mt-1 text-gray-500">
                People can view, download, and <br />
                use their reports.
              </div>
            </div>
          </Menu.Item>
          <Menu.Item
            onClick={() => {
              if (item) {
                unsharePlatformClientResource(
                  {
                    shareTarget: {
                      id: item.shareableId,
                      type: item.shareableType
                    },
                    shareableContent: {
                      id: item.shareableContentId,
                      type: item.shareableContentType
                    }
                  },
                  {
                    onSuccess() {
                      toast(
                        `${getShareableContentName(item)} access removed.`,
                        {
                          variant: 'success'
                        }
                      )
                    },
                    onError(err) {
                      toast(err.message, {
                        variant: 'error'
                      })
                    }
                  }
                )
              }
            }}>
            <div className="w-[150px]">
              <div className="helix-body text-red-600">Remove Access</div>
            </div>
          </Menu.Item>
        </Menu>
      </div>
    </li>
  )
}
